import { Observable, of } from 'rxjs';
import {
  ActiveTableNameType,
  HurdlesAndSpreads,
  ReferenceEntity,
  ReferenceObligation,
  TableColTypes,
  TableCols,
  TableNames,
} from 'src/app/static-data/models/interfaces/static-data';
import {
  ActiveTableKeyType,
  FormattedIssuers,
  IssuerReferenceData,
  StaticDataArrayType,
  StaticDataEntryType,
  TargetMarkets,
  UnderlyingAssets,
  Underlyings,
} from '../models/interfaces/static-data';
import { FormattedEntities } from './../models/interfaces/static-data';

import { Injectable, Injector, ProviderToken } from '@angular/core';
import {
  FormBuilder,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { map, share, switchMap, take } from 'rxjs/operators';
import { ApiService } from 'src/app/auth-o3/shared/services/api.service';
import { PhysicalSettlementMatrix } from 'src/app/form-config/mockups/consts/physical-settlement-matrix.const';
import { IssuerEntity } from 'src/app/product-capture/model/interfaces/issuer-entity.interface';
import { CalendarType } from 'src/app/product-capture/model/interfaces/trading-calendar.interface';
import { environment } from 'src/environments/environment';
import { AGENTS, Agent } from '../models/consts/agents.static-data';
import { CAPITAL_PROTECTION } from '../models/consts/capital-protection';
import { CREDIT_RATING_DESCRIPTIONS } from '../models/consts/credit-rating-descriptions';
import { CREDIT_RATING_OUTLOOKS } from '../models/consts/credit-rating-outlooks';
import { DATA_EXPORT_FORMATS } from '../models/consts/data-export-formats.const';
import { DATA_INGESTION_CONFIG } from '../models/consts/data-ingestion-config.const';
import { DEFAULT_RISK_DESCRIPTIONS } from '../models/consts/default-risk-descriptions';
import { ISSUERS } from '../models/consts/issuers';
import { ISSUERS_ENTITY } from '../models/consts/issuers-entity';
import { LEGAL_PROGRAMMES } from '../models/consts/legal-programmes';
import { ListingVenueColumns as LISTING_VENUE_COLUMNS } from '../models/consts/listing-venue';
import { PAYOFFS } from '../models/consts/payoffs';
import { PHYSICAL_SETTLEMENT_MATRIX_COLUMNS } from '../models/consts/physical-settlement-matrix';
import { REFERENCE_ENTITY } from '../models/consts/reference-entity';
import { REFERENCE_OBLIGATION } from '../models/consts/reference-obligation';
import { STATIC_DATA_TYPE } from '../models/consts/static-data.enum';
import { TARGET_MARKETS } from '../models/consts/target-markets';
import { TRADING_CALENDARS } from '../models/consts/trading-calendars';
import { UKSPA_CODES } from '../models/consts/ukspa-codes';
import { UNDERLYING_ASSETS } from '../models/consts/underlying-assets';
import { UNDERLYINGS } from '../models/consts/underlyings';
import { SdBaseComponent } from '../sd-modal/components/sd-base.component';
import { IssuerIdentifier } from '../sd-modal/components/sd-issuers-entity/issuer-identifier.const';
import { FinancialCalendarService } from './financial-calendar-static-data.service';
import { STATIC_DATA_ENDPOINTS } from './static-data-endpoints.const';
import { StaticDataTableService } from './static-data-table.service';
import { DateUtils } from 'src/app/utils/date.utils';
import { EXCHANGES } from '../models/consts/exchanges';
import { DESKS } from '../models/consts/desk';
import { CONTACTS } from '../models/consts/contact';
import { HURDLES_AND_SPREADS } from '../models/consts/hurdles-and-spreads';

const API_ROOT = environment.api.productCapture;
const HOSTNAME_STATIC_DATA_ENDPOINT = environment.api.staticData;
@Injectable({
  providedIn: 'root',
})
export class StaticDataService {
  activeTableName: ActiveTableNameType;

  activeTableKey: ActiveTableKeyType;

  activeTableForm: UntypedFormGroup;

  _activeTableData: StaticDataArrayType = [];

  activeStaticDataComponent: SdBaseComponent;

  columnDisplayValuesLookup: { [key: string]: TableCols[] } = {
    payoffs: PAYOFFS,
    capitalProtection: CAPITAL_PROTECTION,
    ukspaCodes: UKSPA_CODES,
    underlyings: UNDERLYINGS,
    issuers: ISSUERS,
    creditRatingDescriptions: CREDIT_RATING_DESCRIPTIONS,
    creditRatingOutlooks: CREDIT_RATING_OUTLOOKS,
    defaultRiskDescriptions: DEFAULT_RISK_DESCRIPTIONS,
    tradingCalendars: TRADING_CALENDARS,
    settlementCalendars: TRADING_CALENDARS,
    targetMarkets: TARGET_MARKETS,
    referenceEntity: REFERENCE_ENTITY,
    referenceObligation: REFERENCE_OBLIGATION,
    agents: AGENTS,
    issuersEntity: ISSUERS_ENTITY,
    legalProgrammes: LEGAL_PROGRAMMES,
    listingVenues: LISTING_VENUE_COLUMNS,
    physicalSettlementMatrix: PHYSICAL_SETTLEMENT_MATRIX_COLUMNS,
    underlyingAssets: UNDERLYING_ASSETS,
    exchanges: EXCHANGES,
    dataIngestionConfig: DATA_INGESTION_CONFIG,
    dataExportFormats: DATA_EXPORT_FORMATS,
    desks: DESKS,
    contacts: CONTACTS,
    hurdlesAndSpreads: HURDLES_AND_SPREADS,
  };

  columns: TableCols<TableColTypes>[];

  constructor(
    private apiService: ApiService,
    private router: Router,
    private fb: FormBuilder,
    private sdTableService: StaticDataTableService,
    private injector: Injector
  ) {}

  getTables(): Observable<any> {
    return this.sdTableService.getTables();
  }

  selectTable({ key, displayValue }: TableNames): void {
    this.activeTableKey = key;
    this.activeTableName = displayValue;

    this.columns = this.columnDisplayValuesLookup[key];
    this.createForm();

    const route = this.formatRoute();
    this.router.navigate(['/static-data/', route]);
  }

  createForm(): void {
    const controls = {};

    this.columns?.forEach((name: TableCols) => {
      controls[name.key] =
        this.activeTableKey === 'legalProgrammes'
          ? [
              name.defaultValue !== undefined ? name.defaultValue : '',
              Validators.required,
            ]
          : [
              name.defaultValue !== undefined ? name.defaultValue : null,
              name.validators?.length ? name.validators : null,
            ];
    });

    this.activeTableForm = this.fb.group({ ...controls });

    if (
      this.activeTableKey === 'underlyingAssets' ||
      this.activeTableKey === 'issuersEntity'
    ) {
      this.addFormArray('identifiers');
    }
  }

  addFormArray(fieldName: string): void {
    this.activeTableForm.addControl(fieldName, new UntypedFormArray([]));
  }

  formatRoute(url = this.activeTableKey): string {
    const route = url.replace(/([a-z\xE0-\xFF])([A-Z\xC0\xDF])/g, '$1-$2');
    return route.toLowerCase();
  }

  // Dynamic form row adding speicifically for Underlying Assets > identifiers[]
  get getIdentifiers() {
    return this.activeTableForm.get('identifiers') as UntypedFormArray;
  }

  createIdentifiersRow(): UntypedFormGroup {
    return new UntypedFormGroup({
      type: new UntypedFormControl(null),
      code: new UntypedFormControl(null),
    });
  }

  addIdentifiersRow(): void {
    this.getIdentifiers.push(this.createIdentifiersRow());
  }

  // ** POST **

  addLegalProgrammeRecordTest(): Observable<StaticDataEntryType> {
    const newRecord = {
      issuerId: 1,
      name: 'TestABC',
      description: 'TestABC',
      programmeDate: {
        year: 2024,
        month: 6,
        day: 12,
        hour: 0,
        minute: 0,
      },
    };

    const payloadWithDateAdjustment = {
      ...newRecord,
      programmeDate: DateUtils.getStringFromDateObject(
        newRecord.programmeDate as any
      ),
    };

    console.log('payloadWithDateAdjustment', payloadWithDateAdjustment);

    return this.apiService.post(
      STATIC_DATA_ENDPOINTS.LEGAL_PROGRAMMES,
      payloadWithDateAdjustment,
      HOSTNAME_STATIC_DATA_ENDPOINT
    );
  }

  addRecord(formValues: any): Observable<StaticDataEntryType> {
    const tableKey = this.activeTableKey;

    const newRecord = {
      ...formValues,
    };

    if (tableKey === STATIC_DATA_TYPE.PAYOFFS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.PAYOFF,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CAPITAL_PROTECTION) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.CAPITAL_PROTECTION,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UKSPA_CODES) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.UKSPA_PRODUCT_CODES,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYINGS) {
      const newUnderlyingsRecord = {
        ...formValues,
        referenceAsset: formValues.referenceAsset.id,
        underlyingAssetId: formValues.referenceAsset.underlyingAssetId,
      };

      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.UNDERLYING_REFERENCE_DATA, // using old endpoint
        newUnderlyingsRecord,
        API_ROOT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.EXCHANGES) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.EXCHANGES,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    // OK but missing 'website' property
    if (tableKey === STATIC_DATA_TYPE.ISSUERS) {
      const newIssuersRecord = {
        ...formValues,
        issuerId: formValues.longName.issuerId,
        longName: formValues.longName.id,
      };
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.ISSUERS,
        newIssuersRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_DESCRIPTIONS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.CREDIT_RATING,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_OUTLOOKS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.CREDIT_OUTLOOK,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DEFAULT_RISK_DESCRIPTIONS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.DEFAULT_RISK,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (
      tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS ||
      tableKey === STATIC_DATA_TYPE.SETTLEMENT_CALENDARS
    ) {
      return this.getService(FinancialCalendarService).add({
        ...formValues,
        type:
          tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS
            ? CalendarType.TRADING
            : CalendarType.SETTLEMENT,
      });
    }

    if (tableKey === STATIC_DATA_TYPE.TARGET_MARKETS) {
      const { payoff: payoffId, ...rest } = newRecord;

      const newTargetMarket = {
        payoffId,
        ...rest,
      };

      return this.apiService
        .post(
          STATIC_DATA_ENDPOINTS.TARGET_MARKET,
          newTargetMarket,
          HOSTNAME_STATIC_DATA_ENDPOINT
        )
        .pipe<any>(take(1));
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_ENTITY) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.REFERENCE_ENTITY,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_OBLIGATION) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.REFERENCE_OBLIGATION,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.ISSUERS_ENTITY) {
      const { identifiers, ...rest } = newRecord;
      const validIdentifiers = identifiers.filter(
        (item) => item?.type && item.code
      );

      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.ISSUER_ENTITY,
        {
          ...rest,
          identifiers: validIdentifiers,
        },
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LEGAL_PROGRAMMES) {
      const payloadWithDateAdjustment = {
        ...newRecord,
        programmeDate: DateUtils.getStringFromDateObject(
          newRecord.programmeDate
        ),
      };

      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.LEGAL_PROGRAMMES,
        payloadWithDateAdjustment,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LISTING_VENUES) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.LISTING_VENUES,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.AGENTS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.AGENTS,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PHYSICAL_SETTLEMENT_MATRIX) {
      newRecord.id = newRecord.transactionType.toUpperCase();

      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.PHYSICAL_SETTLEMENT_MATRIX,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYING_ASSETS) {
      const { identifiers, ...rest } = newRecord;
      const validIdentifiers = identifiers.filter(
        (item) => item?.type && item.code
      );

      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.UNDERLYING_ASSETS,
        {
          ...rest,
          identifiers: validIdentifiers,
        },
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_INGESTION_CONFIG) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.DATA_INGESTION_CONFIG,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_EXPORT_FORMATS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.DATA_EXPORT_FORMATS,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DESKS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.DESKS,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CONTACTS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.CONTACTS,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.HURDLES_AND_SPREADS) {
      const recordDto = { ...newRecord };
      recordDto.asOfDate = DateUtils.formatDate(
        new Date(
          `${recordDto.asOfDate.month}/
            ${recordDto.asOfDate.day}/
            ${recordDto.asOfDate.year}`
        ),
        'DD-MM-YYYY'
      );

      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.HURDLES_AND_SPREADS,
        recordDto,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }
  }

  // ** DELETE **
  deleteRecord(row: any): Observable<StaticDataEntryType> {
    const tableKey = this.activeTableKey;

    if (tableKey === STATIC_DATA_TYPE.PAYOFFS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.PAYOFF}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CAPITAL_PROTECTION) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.CAPITAL_PROTECTION}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UKSPA_CODES) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.UKSPA_PRODUCT_CODES}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYINGS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.UNDERLYING_REFERENCE_DATA}/${row.underlyingAssetId}`,
        API_ROOT
      );
    }

    // ???? 500
    if (tableKey === STATIC_DATA_TYPE.ISSUERS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.ISSUERS}/${row.issuerId}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_DESCRIPTIONS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.CREDIT_RATING}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_OUTLOOKS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.CREDIT_OUTLOOK}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DEFAULT_RISK_DESCRIPTIONS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.DEFAULT_RISK}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (
      tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS ||
      tableKey === STATIC_DATA_TYPE.SETTLEMENT_CALENDARS
    ) {
      return this.getService(FinancialCalendarService).delete({
        ...row,
        type:
          tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS
            ? CalendarType.TRADING
            : CalendarType.SETTLEMENT,
      });
    }

    if (tableKey === STATIC_DATA_TYPE.TARGET_MARKETS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.TARGET_MARKET}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_ENTITY) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.REFERENCE_ENTITY}/${row.referenceEntityID}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_OBLIGATION) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.REFERENCE_OBLIGATION}/${row.referenceObligationID}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.ISSUERS_ENTITY) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.ISSUER_ENTITY}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LEGAL_PROGRAMMES) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.LEGAL_PROGRAMMES}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LISTING_VENUES) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.LISTING_VENUES}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.AGENTS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.AGENTS}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PHYSICAL_SETTLEMENT_MATRIX) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.PHYSICAL_SETTLEMENT_MATRIX}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYING_ASSETS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.UNDERLYING_ASSETS}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.EXCHANGES) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.EXCHANGES}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_INGESTION_CONFIG) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.DATA_INGESTION_CONFIG}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_EXPORT_FORMATS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.DATA_EXPORT_FORMATS}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DESKS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.DESKS}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CONTACTS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.CONTACTS}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.HURDLES_AND_SPREADS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.HURDLES_AND_SPREADS}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }
  }

  // ** PUT **
  editRecord(row, formValues): Observable<StaticDataEntryType> {
    const tableKey = this.activeTableKey;

    const editedRecord = {
      id: row.id,
      ...formValues,
    };

    if (tableKey === STATIC_DATA_TYPE.PAYOFFS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.PAYOFF}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CAPITAL_PROTECTION) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.CAPITAL_PROTECTION}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UKSPA_CODES) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.UKSPA_PRODUCT_CODES}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYINGS) {
      const editedUnderlyings = {
        underlyingAssetId: row.underlyingAssetId,
        referenceAsset: row.referenceAsset,
        ...formValues,
      };
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.UNDERLYING_REFERENCE_DATA}/${row.underlyingAssetId}`,
        editedUnderlyings,
        API_ROOT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.ISSUERS) {
      const editedIssuerRecord = {
        issuerId: row.issuerId,
        ...formValues,
      };
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.ISSUERS}/${row.issuerId}`,
        editedIssuerRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_DESCRIPTIONS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.CREDIT_RATING}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_OUTLOOKS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.CREDIT_OUTLOOK}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DEFAULT_RISK_DESCRIPTIONS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.DEFAULT_RISK}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (
      tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS ||
      tableKey === STATIC_DATA_TYPE.SETTLEMENT_CALENDARS
    ) {
      return this.getService(FinancialCalendarService).edit(row, {
        ...editedRecord,
        type:
          tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS
            ? CalendarType.TRADING
            : CalendarType.SETTLEMENT,
      });
    }

    if (tableKey === STATIC_DATA_TYPE.TARGET_MARKETS) {
      const { payoffId, id } = row;
      const { payoff, ...rest } = editedRecord;

      const newEditedRecord = {
        payoffId,
        ...rest,
      };

      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.TARGET_MARKET}/${id}`,
        newEditedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_ENTITY) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.REFERENCE_ENTITY}/${row.referenceEntityID}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_OBLIGATION) {
      const { id, ...rest } = editedRecord;
      const newRecord = { ...rest };

      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.REFERENCE_OBLIGATION}/${row.referenceObligationID}`,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.ISSUERS_ENTITY) {
      let { identifiers, ...rest } = editedRecord;
      identifiers = identifiers.filter(
        (item: IssuerIdentifier) => item?.type && item.code
      );

      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.ISSUER_ENTITY}/${row.id}`,
        {
          ...rest,
          ...(identifiers.length && { identifiers }),
        },
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LEGAL_PROGRAMMES) {
      const editWithDateAdjustment = {
        ...editedRecord,
        programmeDate: DateUtils.getStringFromDateObject(
          editedRecord.programmeDate
        ),
      };

      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.LEGAL_PROGRAMMES}/${row.id}`,
        editWithDateAdjustment,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LISTING_VENUES) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.LISTING_VENUES}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.AGENTS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.AGENTS}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PHYSICAL_SETTLEMENT_MATRIX) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.PHYSICAL_SETTLEMENT_MATRIX}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYING_ASSETS) {
      const { identifiers, ...rest } = editedRecord;
      const validIdentifiers = identifiers.filter(
        (item) => item?.type && item.code
      );

      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.UNDERLYING_ASSETS}/${row.id}`,
        {
          ...rest,
          identifiers: validIdentifiers,
        },
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.EXCHANGES) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.EXCHANGES}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_INGESTION_CONFIG) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.DATA_INGESTION_CONFIG}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_EXPORT_FORMATS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.DATA_EXPORT_FORMATS}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DESKS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.DESKS}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CONTACTS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.CONTACTS}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.HURDLES_AND_SPREADS) {
      const recordDto = { ...editedRecord };
      recordDto.asOfDate = DateUtils.formatDate(
        new Date(
          `${recordDto.asOfDate.month}/
            ${recordDto.asOfDate.day}/
            ${recordDto.asOfDate.year}`
        ),
        'DD-MM-YYYY'
      );

      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.HURDLES_AND_SPREADS}/${row.id}`,
        recordDto,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }
  }

  // ** GET **
  getData(type?: ActiveTableKeyType): Observable<StaticDataArrayType> {
    if (!type) {
      type = this.activeTableKey;
    }

    if (type === STATIC_DATA_TYPE.PAYOFFS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.PAYOFFS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.CAPITAL_PROTECTION) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.CAPITAL_PROTECTIONS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.UKSPA_CODES) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.UKSPA_PRODUCT_CODES,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.UNDERLYINGS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.UNDERLYING_REFERENCE_DATA,
        API_ROOT
      );
    }

    if (type === STATIC_DATA_TYPE.ISSUERS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.ISSUERS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.CREDIT_RATING_DESCRIPTIONS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.CREDIT_RATINGS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.CREDIT_RATING_OUTLOOKS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.CREDIT_OUTLOOKS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.DEFAULT_RISK_DESCRIPTIONS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.DEFAULT_RISKS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.TRADING_CALENDARS) {
      return this.getService(FinancialCalendarService).getAllOfType(
        CalendarType.TRADING
      );
    }

    if (type === STATIC_DATA_TYPE.SETTLEMENT_CALENDARS) {
      return this.getService(FinancialCalendarService).getAllOfType(
        CalendarType.SETTLEMENT
      );
    }

    if (type === STATIC_DATA_TYPE.TARGET_MARKETS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.TARGET_MARKET,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.REFERENCE_ENTITY) {
      return this.apiService
        .get(
          STATIC_DATA_ENDPOINTS.REFERENCE_ENTITY,
          HOSTNAME_STATIC_DATA_ENDPOINT
        )
        .pipe(
          map((data: ReferenceEntity[]) =>
            data.sort((a: ReferenceEntity, b: ReferenceEntity) =>
              a.longName.localeCompare(b.longName)
            )
          ),
          share<ReferenceEntity[]>()
        );
    }

    if (type === STATIC_DATA_TYPE.REFERENCE_OBLIGATION) {
      return this.apiService
        .get(
          STATIC_DATA_ENDPOINTS.REFERENCE_OBLIGATION,
          HOSTNAME_STATIC_DATA_ENDPOINT
        )
        .pipe(take<ReferenceObligation[]>(1));
    }

    if (type === STATIC_DATA_TYPE.ISSUERS_ENTITY) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.ISSUERS_ENTITY,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.LEGAL_PROGRAMMES) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.LEGAL_PROGRAMMES,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.LISTING_VENUES) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.LISTING_VENUES,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.AGENTS) {
      return this.apiService
        .get(`${STATIC_DATA_ENDPOINTS.AGENTS}s`, HOSTNAME_STATIC_DATA_ENDPOINT)
        .pipe(take<Agent[]>(1));
    }

    if (type === STATIC_DATA_TYPE.CURRENCIES) {
      return this.apiService.get(STATIC_DATA_ENDPOINTS.CURRENCIES, API_ROOT);
    }

    if (type === STATIC_DATA_TYPE.PHYSICAL_SETTLEMENT_MATRIX) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.PHYSICAL_SETTLEMENT_MATRIX,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.UNDERLYING_ASSETS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.UNDERLYING_ASSETS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.EXCHANGES) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.EXCHANGES,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.DATA_INGESTION_CONFIG) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.DATA_INGESTION_CONFIG,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.DATA_EXPORT_FORMATS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.DATA_EXPORT_FORMATS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.DESKS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.DESKS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.CONTACTS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.CONTACTS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.HURDLES_AND_SPREADS) {
      return this.apiService
        .get(
          STATIC_DATA_ENDPOINTS.HURDLES_AND_SPREADS,
          HOSTNAME_STATIC_DATA_ENDPOINT
        )
        .pipe(
          map((data: HurdlesAndSpreads[]) => {
            return data.map((item) => {
              item.asOfDate = DateUtils.convertStringToNgbDate(
                item.asOfDate,
                'DD-MM-YYYY'
              );
              return item;
            });
          })
        );
    }
  }

  filterUnderlyingAssetsTableData(
    underlyingAssets: UnderlyingAssets[],
    underlyingReferenceData: Underlyings[]
  ): Array<UnderlyingAssets> {
    return underlyingAssets.filter((item) => {
      return !underlyingReferenceData.some(
        (entry) => entry.underlyingAssetId === item.id
      );
    });
  }

  getIssuersTable(): Observable<Array<IssuerEntity>> {
    return this.apiService.get(
      STATIC_DATA_TYPE.ISSUERS,
      HOSTNAME_STATIC_DATA_ENDPOINT
    );
  }

  getReferenceEntityDataById(id: number): Observable<ReferenceEntity> {
    return this.apiService.get(
      `${STATIC_DATA_ENDPOINTS.REFERENCE_ENTITY}/${id}`,
      HOSTNAME_STATIC_DATA_ENDPOINT
    );
  }

  filterIssuersTableData(
    issuers: IssuerEntity[],
    issuerReferenceData: IssuerReferenceData[]
  ): Array<IssuerEntity> {
    return issuers.filter((item) => {
      return !issuerReferenceData.some((entry) => entry.issuerId === item.id);
    });
  }

  formatIssuersTableDataItems(
    filteredIssuers: IssuerEntity[]
  ): FormattedIssuers[] {
    return filteredIssuers.map((item) => {
      return {
        id: { id: item.entityName, issuerId: item.id },
        displayValue: item.entityName,
      };
    });
  }

  formatRefEntityTableItems(
    refEntityList: ReferenceEntity[]
  ): FormattedEntities[] {
    return refEntityList.map((item) => {
      return {
        id: item.referenceEntityID,
        displayValue: item.longName,
      };
    });
  }

  formatIssuerEntityTableItems(issuerEntityList: any): any {
    return issuerEntityList.map((item) => {
      return {
        id: item.id,
        displayValue: item.entityName,
      };
    });
  }

  formatExchangeItems(exchanges: any): any {
    return exchanges.map((item) => {
      return {
        id: item.id,
        displayValue: item.micCode
          ? `${item.micCode} | ${item.longName}`
          : item.longName,
      };
    });
  }

  formatDeskItems(desks: any): any {
    return desks.map((item) => {
      return {
        id: item.id,
        displayValue: item.name,
      };
    });
  }

  getPhysicalMatrixDataByTransactionType(
    transactionType: string
  ): Observable<PhysicalSettlementMatrix> {
    return this.apiService
      .get<PhysicalSettlementMatrix[]>(
        `${
          STATIC_DATA_ENDPOINTS.PHYSICAL_SETTLEMENT_MATRIX
        }?transactionType=${encodeURIComponent(transactionType)}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      )
      .pipe(
        switchMap((data: PhysicalSettlementMatrix[]) => of(data[0])),
        take<PhysicalSettlementMatrix>(1)
      );
  }

  // Helper funtions
  getService<T>(service: ProviderToken<T>): T {
    return this.injector.get<T>(service);
  }

  stringifyBooleans(arrayOfObjects: TargetMarkets[]): Array<any> {
    return arrayOfObjects.map((obj) => {
      for (const prop in obj) {
        if (obj[prop] === true) obj[prop] = 'True';
        if (obj[prop] === false) obj[prop] = 'False';
        else obj[prop];
      }
      return obj;
    });
  }

  stringToBoolConversion(string) {
    if (string == undefined || string === '') {
      return string;
    }
    return string.toLowerCase().includes('true') ? true : false;
  }
}
